import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// ── Points / level data ────────────────────────────────────────────────────
const LEVEL_POINTS = { spark: 10, depth: 25, breakthrough: 50 };
const DAILY_BONUS = 15;

const PROGRESSION = [
  { min: 0,    title: 'Curious' },
  { min: 201,  title: 'Present' },
  { min: 501,  title: 'Connected' },
  { min: 1001, title: 'Signal' },
  { min: 2501, title: 'Rare' },
];

const getTitle = (pts) =>
  [...PROGRESSION].reverse().find(l => pts >= l.min)?.title ?? 'Curious';

const estimatePoints = (talkCount, level) =>
  Math.max(talkCount, 1) * (LEVEL_POINTS[level] || 10) + DAILY_BONUS;

// ── Mini inline email capture ──────────────────────────────────────────────
const MiniEmailForm = ({ onSuccess, submitLabel = 'Send me a link' }) => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { sendMagicLink } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await sendMagicLink(email.trim());
      setSent(true);
      setTimeout(() => onSuccess?.(), 2000);
    } catch {
      // fail silently — user sees the "check inbox" state
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-3"
        data-testid="motivation-sent-confirmation"
      >
        <CheckCircle size={32} className="text-orange-primary mx-auto mb-2" />
        <p className="text-charcoal text-sm font-medium">Check your inbox</p>
        <p className="text-charcoal/50 text-xs mt-0.5">Click the link in the email to sign in</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <div className="relative flex-1 min-w-0">
        <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/35 pointer-events-none" />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          required
          autoFocus
          className="w-full pl-8 pr-3 py-2.5 rounded-xl border border-charcoal/12 bg-white/70 focus:outline-none focus:border-orange-primary/50 transition-colors"
          style={{ fontSize: '16px' }}
          data-testid="motivation-email-input"
        />
      </div>
      <motion.button
        type="submit"
        disabled={loading || !email.trim()}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="px-4 py-2.5 rounded-xl font-medium text-white text-sm disabled:opacity-50 whitespace-nowrap flex-shrink-0"
        style={{ background: '#ff780a', boxShadow: '0 3px 12px rgba(255,120,10,0.25)' }}
        data-testid="motivation-submit-btn"
      >
        {loading ? '...' : submitLabel}
      </motion.button>
    </form>
  );
};

// ── Value Moment Card ──────────────────────────────────────────────────────
// Appears at thought 4 — thought still loads behind it, never blocking
export const ValueMomentCard = ({ onDismiss, talkCount, currentLevel }) => {
  const pts = estimatePoints(talkCount, currentLevel);
  const levelTitle = getTitle(pts);
  const conversationCount = Math.max(talkCount, 3);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 12 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="fixed inset-x-0 bottom-0 sm:bottom-32 z-50 flex justify-center px-4 pb-4 sm:pb-0 pointer-events-none"
        data-testid="value-moment-overlay"
      >
        <motion.div
          className="pointer-events-auto w-full max-w-sm rounded-2xl p-6 relative"
          style={{
            background: 'rgba(255, 248, 240, 0.97)',
            boxShadow: '0 8px 48px rgba(44, 36, 23, 0.14)',
            border: '1px solid rgba(255, 120, 10, 0.15)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Dismiss */}
          <button
            onClick={onDismiss}
            className="absolute top-3 right-3 p-1.5 rounded-full text-charcoal/25 hover:text-charcoal/55 hover:bg-charcoal/5 transition-colors"
            data-testid="value-moment-dismiss-x"
          >
            <X size={15} />
          </button>

          {/* Level teaser badge */}
          <div className="mb-3">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
              style={{ background: 'rgba(255,120,10,0.1)', color: '#ff780a' }}
              data-testid="value-moment-level-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#ff780a' }} />
              You'd already be: <strong>{levelTitle}</strong>
            </span>
          </div>

          <p className="text-charcoal font-serif text-lg leading-snug mb-1">
            You've had {conversationCount} conversations today.
          </p>
          <p className="text-charcoal/60 text-sm leading-relaxed mb-4">
            Sign in to claim your 1-day streak and{' '}
            <span className="text-charcoal font-medium">{pts} points</span>
            {' '}— already at{' '}
            <strong className="text-charcoal">{levelTitle}</strong>.
            {' '}Drop your email below and we'll send you a link — works whether you've been here before or not.
          </p>

          <MiniEmailForm onSuccess={onDismiss} submitLabel="Send me a link" />

          <button
            onClick={onDismiss}
            className="mt-3 w-full text-center text-xs text-charcoal/35 hover:text-charcoal/55 transition-colors"
            data-testid="value-moment-not-now"
          >
            Not now
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ── Soft Gate Overlay ──────────────────────────────────────────────────────
// At thought 7+. Slides up from bottom like ValueMomentCard. Dismissible.
export const SoftGateOverlay = ({ thoughtCount, onContinue }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 12 }}
    transition={{ duration: 0.4, ease: 'easeOut' }}
    className="fixed inset-x-0 bottom-0 sm:bottom-32 z-50 flex justify-center px-4 pb-4 sm:pb-0 pointer-events-none"
    data-testid="soft-gate-overlay"
  >
    <motion.div
      className="pointer-events-auto w-full max-w-sm rounded-2xl p-6 relative"
      style={{
        background: 'rgba(255, 248, 240, 0.97)',
        boxShadow: '0 8px 48px rgba(44, 36, 23, 0.14)',
        border: '1px solid rgba(255, 120, 10, 0.15)',
        backdropFilter: 'blur(10px)',
      }}
    >
      {/* Dismiss X */}
      <button
        onClick={onContinue}
        className="absolute top-3 right-3 p-1.5 rounded-full text-charcoal/25 hover:text-charcoal/55 hover:bg-charcoal/5 transition-colors"
        data-testid="soft-gate-dismiss-x"
      >
        <X size={15} />
      </button>

      <p className="text-charcoal font-serif text-lg leading-snug mb-2 pr-6">
        These conversations deserve to be remembered.
      </p>
      <p className="text-charcoal/60 text-sm leading-relaxed mb-4">
        You've already explored {thoughtCount} thoughts today. Sign in and we'll keep track of your conversations and your depth over time — all in your own culture diary.
      </p>

      <MiniEmailForm onSuccess={onContinue} submitLabel="Send me a link" />

      <p className="text-charcoal/30 text-xs mt-3 mb-3">Just your email. No password. Ever.</p>

      <button
        onClick={onContinue}
        className="w-full text-center text-xs text-charcoal/35 hover:text-charcoal/55 transition-colors"
        data-testid="soft-gate-not-now"
      >
        Not now
      </button>
    </motion.div>
  </motion.div>
);
